import React, { useMemo, useState } from 'react';
import { MdAdd, MdArrowBackIos, MdExpandMore, MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import ExportButton from './ExportButton';

const VIEW_TYPES = {
    TRANSACTION: 'guest_transaction',
    GUEST: 'guest_list',
};

const TX_COL_WIDTHS = [56, 220, 90, 130, 140, 260, 100, 80, 80];
const GUEST_COL_WIDTHS = [56, 150, 180, 130, 140, 120, 120, 140, 130, 130, 120, 120, 120, 130, 170];

const toNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatCurrency0 = (value) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Math.round(toNum(value)));
};

const formatCurrency2 = (value) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(toNum(value));
};

const asText = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
};

// Demo Data - 5 Transactions
const DEMO_TRANSACTIONS = [
    {
        _id: 'tx1',
        item_id: 'ITEM-001',
        check_number: 1001,
        order_date: '2024-01-15T10:30:00Z',
        client: 'John Doe',
        description: 'Dinner for two - Steak and Wine',
        amount: 125.50,
        qty: 2,
        is_tax: true,
    },
    {
        _id: 'tx2',
        item_id: 'ITEM-002',
        check_number: 1002,
        order_date: '2024-01-16T14:15:00Z',
        client: 'Jane Smith',
        description: 'Business lunch - Salad and Coffee',
        amount: 45.00,
        qty: 1,
        is_tax: false,
    },
    {
        _id: 'tx3',
        item_id: 'ITEM-003',
        check_number: 1003,
        order_date: '2024-01-17T19:45:00Z',
        client: 'Mike Johnson',
        description: 'Family dinner - Pasta and Desserts',
        amount: 89.99,
        qty: 4,
        is_tax: true,
    },
    {
        _id: 'tx4',
        item_id: 'ITEM-004',
        check_number: 1004,
        order_date: '2024-01-18T12:00:00Z',
        client: 'Sarah Williams',
        description: 'Brunch - Pancakes and Orange Juice',
        amount: 32.25,
        qty: 1,
        is_tax: false,
    },
    {
        _id: 'tx5',
        item_id: 'ITEM-005',
        check_number: 1005,
        order_date: '2024-01-19T20:30:00Z',
        client: 'David Brown',
        description: 'Anniversary dinner - Lobster and Champagne',
        amount: 250.00,
        qty: 2,
        is_tax: true,
    },
];

// Demo Data - 5 Guests
const DEMO_GUESTS = [
    {
        _id: 'g1',
        guest_name: 'John Doe',
        email: 'john.doe@email.com',
        marketing_opt_in: true,
        company: 'Tech Corp',
        birthday: '1985-03-15',
        anniversary: '2010-06-20',
        phone_number: '+1-555-0101',
        first_visit_date: '2023-01-10',
        recent_visit_date: '2024-01-15',
        completed_visits: 15,
        cancelled_count: 2,
        no_show_count: 1,
        lifetime_spend: 1250.50,
        lifetime_per_cover_spend: 83.37,
    },
    {
        _id: 'g2',
        guest_name: 'Jane Smith',
        email: 'jane.smith@email.com',
        marketing_opt_in: false,
        company: 'Design Studio',
        birthday: '1990-07-22',
        anniversary: null,
        phone_number: '+1-555-0102',
        first_visit_date: '2023-03-05',
        recent_visit_date: '2024-01-16',
        completed_visits: 8,
        cancelled_count: 0,
        no_show_count: 0,
        lifetime_spend: 450.00,
        lifetime_per_cover_spend: 56.25,
    },
    {
        _id: 'g3',
        guest_name: 'Mike Johnson',
        email: 'mike.j@email.com',
        marketing_opt_in: true,
        company: 'Finance Inc',
        birthday: '1978-11-08',
        anniversary: '2005-09-12',
        phone_number: '+1-555-0103',
        first_visit_date: '2022-06-15',
        recent_visit_date: '2024-01-17',
        completed_visits: 25,
        cancelled_count: 3,
        no_show_count: 2,
        lifetime_spend: 2100.75,
        lifetime_per_cover_spend: 84.03,
    },
    {
        _id: 'g4',
        guest_name: 'Sarah Williams',
        email: 'sarah.w@email.com',
        marketing_opt_in: true,
        company: 'Marketing Pro',
        birthday: '1992-04-30',
        anniversary: null,
        phone_number: '+1-555-0104',
        first_visit_date: '2023-08-20',
        recent_visit_date: '2024-01-18',
        completed_visits: 6,
        cancelled_count: 1,
        no_show_count: 0,
        lifetime_spend: 320.50,
        lifetime_per_cover_spend: 53.42,
    },
    {
        _id: 'g5',
        guest_name: 'David Brown',
        email: 'david.brown@email.com',
        marketing_opt_in: false,
        company: 'Legal Services',
        birthday: '1980-12-25',
        anniversary: '2008-02-14',
        phone_number: '+1-555-0105',
        first_visit_date: '2022-11-30',
        recent_visit_date: '2024-01-19',
        completed_visits: 30,
        cancelled_count: 1,
        no_show_count: 1,
        lifetime_spend: 3500.00,
        lifetime_per_cover_spend: 116.67,
    },
];

// Demo Analysis Data
const DEMO_ANALYSIS = {
    today_sales: 542.75,
    yesterday_sales: 890.50,
    this_week_sales: 3250.25,
    this_month_sales: 12500.00,
    this_year_sales: 45000.00,
    reports_count: 5,
};

const DailySalesHome = () => {
    const navigate = useNavigate();

    const handleGoBack = () => {
        navigate(-1); // Go back to previous page
    };

    const [sortField, setSortField] = useState('order_date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [activeViewType, setActiveViewType] = useState(VIEW_TYPES.TRANSACTION);
    const [expandedCards, setExpandedCards] = useState(new Set());

    const handleSort = (field) => {
        setSortOrder(sortField === field && sortOrder === 'asc' ? 'desc' : 'asc');
        setSortField(field);
    };

    const toggleCard = (cardId) => {
        setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cardId)) {
                newSet.delete(cardId);
            } else {
                newSet.add(cardId);
            }
            return newSet;
        });
    };

    const expandAllCards = () => {
        const allIds = sortedData.map((row, index) => row._id || `card-${index}`);
        setExpandedCards(new Set(allIds));
    };

    const collapseAllCards = () => {
        setExpandedCards(new Set());
    };

    const isCardExpanded = (cardId) => expandedCards.has(cardId);

    const activeData = activeViewType === VIEW_TYPES.GUEST ? DEMO_GUESTS : DEMO_TRANSACTIONS;

    const sortedData = useMemo(() => {
        const data = [...activeData];
        data.sort((a, b) => {
            const aValue = a?.[sortField];
            const bValue = b?.[sortField];

            if (['order_date', 'birthday', 'anniversary', 'first_visit_date', 'recent_visit_date'].includes(sortField)) {
                const aDate = aValue ? new Date(aValue).getTime() : 0;
                const bDate = bValue ? new Date(bValue).getTime() : 0;
                return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
            }

            if (['amount', 'qty', 'check_number', 'completed_visits', 'cancelled_count', 'no_show_count', 'lifetime_spend', 'lifetime_per_cover_spend'].includes(sortField)) {
                return sortOrder === 'asc' ? toNum(aValue) - toNum(bValue) : toNum(bValue) - toNum(aValue);
            }

            const aStr = (aValue || '').toString().toLowerCase();
            const bStr = (bValue || '').toString().toLowerCase();
            return sortOrder === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        });
        return data;
    }, [activeData, sortField, sortOrder]);

    const totals = useMemo(() => {
        if (activeViewType === VIEW_TYPES.GUEST) {
            return sortedData.reduce(
                (acc, row) => {
                    acc.completed_visits += toNum(row?.completed_visits);
                    acc.lifetime_spend += toNum(row?.lifetime_spend);
                    return acc;
                },
                { completed_visits: 0, lifetime_spend: 0 }
            );
        }
        return sortedData.reduce(
            (acc, row) => {
                acc.qty += toNum(row?.qty);
                acc.amount += toNum(row?.amount);
                return acc;
            },
            { qty: 0, amount: 0 }
        );
    }, [activeViewType, sortedData]);

    const getCellStyle = (viewType, idx) => {
        const widths = viewType === VIEW_TYPES.TRANSACTION ? TX_COL_WIDTHS : GUEST_COL_WIDTHS;
        const width = widths[idx] || 130;
        return {
            flex: `0 0 ${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
        };
    };
    const minRowWidth = activeViewType === VIEW_TYPES.TRANSACTION
        ? TX_COL_WIDTHS.reduce((sum, n) => sum + n, 0)
        : GUEST_COL_WIDTHS.reduce((sum, n) => sum + n, 0);
    const rowPadStyle = { padding: '0 12px', margin: '0' };

    return (
        <div className="createRoomPopup animate__animated animate__zoomIn animation_duration bgSurfaceDark">
            <header className="filterFileHeader">
                <div className="allfilterFileHeader !items-center">
                    <MdArrowBackIos
                        onClick={handleGoBack}
                        className="allFileHeaderIcon thread_eHeaderIcon text-2xl !-mt-0 cursor-pointer hover:opacity-80"
                    />
                    <span className="allFileHeaderText !mt-0">Daily Sales</span>
                </div>
                <div className="headTools">
                    <MdAdd className='text-2xl cursor-pointer text-white mr-3 -mt-2' />
                </div>
            </header>
            <div className='right_container task p-4' style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>


                {/* Demo Header */}
                <div className='salesReportTabCon' style={{ height: 'auto', overflow: 'visible', flex: '0 0 auto' }}>
                    <div className='salesReportMainCon'>
                        <div className='flex items-center justify-between mb-4'>
                            {/* Modern Toggle Switch */}
                            <div className='flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 shadow-inner'>
                                <button
                                    onClick={() => setActiveViewType(VIEW_TYPES.TRANSACTION)}
                                    className={`px-2.5 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                                        activeViewType === VIEW_TYPES.TRANSACTION
                                            ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm'
                                            : 'text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                >
                                    <span className='flex items-center gap-1'>
                                        <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' />
                                        </svg>
                                        Transactions
                                    </span>
                                </button>
                                <button
                                    onClick={() => setActiveViewType(VIEW_TYPES.GUEST)}
                                    className={`px-2.5 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                                        activeViewType === VIEW_TYPES.GUEST
                                            ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm'
                                            : 'text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                >
                                    <span className='flex items-center gap-1'>
                                        <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' />
                                        </svg>
                                        Guests
                                    </span>
                                </button>
                            </div>
                            <div className='flex-1' />
                            <ExportButton />
                        </div>
                        {/* Mobile: Horizontal scrollable / Desktop: Grid layout */}
                        <div className='flex md:grid md:grid-cols-2 gap-3 overflow-x-auto mb-3 md:mb-0 md:overflow-visible snap-x snap-mandatory scrollbar-hide'>
                            <div className='salesReportGraphBox flex-shrink-0 w-[200px] md:w-auto snap-start'>
                                <div className='salesReportBox'>
                                    <p className='salesReportIcon'>$</p>
                                    <p className='salesReportNumber'>{formatCurrency0(DEMO_ANALYSIS.today_sales)}</p>
                                </div>
                                <p className='salesReportText'>Today&apos;s sales</p>
                            </div>
                            <div className='salesReportGraphBox flex-shrink-0 w-[200px] md:w-auto snap-start'>
                                <div className='salesReportBox'>
                                    <p className='salesReportIcon'>$</p>
                                    <p className='salesReportNumber'>{formatCurrency0(DEMO_ANALYSIS.yesterday_sales)}</p>
                                </div>
                                <p className='salesReportText'>Yesterday&apos;s sales</p>
                            </div>
                            <div className='salesReportGraphBox flex-shrink-0 w-[200px] md:w-auto snap-start'>
                                <div className='salesReportBox'>
                                    <p className='salesReportIcon'>$</p>
                                    <p className='salesReportNumber'>{formatCurrency0(DEMO_ANALYSIS.this_week_sales)}</p>
                                </div>
                                <p className='salesReportText'>This week till date</p>
                            </div>
                            <div className='salesReportGraphBox flex-shrink-0 w-[200px] md:w-auto snap-start'>
                                <div className='salesReportBox'>
                                    <p className='salesReportIcon'>$</p>
                                    <p className='salesReportNumber'>{formatCurrency0(DEMO_ANALYSIS.this_month_sales)}</p>
                                </div>
                                <p className='salesReportText'>This month till date</p>
                            </div>
                            <div className='salesReportGraphBox flex-shrink-0 w-[200px] md:w-auto snap-start'>
                                <div className='salesReportBox'>
                                    <p className='salesReportIcon'>$</p>
                                    <p className='salesReportNumber'>{formatCurrency0(DEMO_ANALYSIS.this_year_sales)}</p>
                                </div>
                                <p className='salesReportText'>This year till date</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className='task_body_area inReportArea taskMainListTableCon mt-2 md:mt-5'
                    style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', position: 'relative' }}
                >
                    {/* Mobile Card View */}

                    {/* Expand/Collapse All Controls */}
                    {/* {sortedData.length > 0 && (
                            <div className='flex justify-end gap-2 mb-3 px-1'>
                                <button
                                    onClick={expandAllCards}
                                    className='text-xs px-3 py-1.5 rounded-full bg-cerulean-100 text-cerulean-700 hover:bg-cerulean-200 transition-colors duration-200 font-medium'
                                    aria-label="Expand all cards"
                                >
                                    Expand All
                                </button>
                                <button
                                    onClick={collapseAllCards}
                                    className='text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors duration-200 font-medium'
                                    aria-label="Collapse all cards"
                                >
                                    Collapse All
                                </button>
                            </div>
                        )} */}

                    {sortedData.length > 0 ? (
                        <div className='space-y-3 ppb-4 !h-[calc(100vh-240px)] overflow-auto'>
                            {activeViewType === VIEW_TYPES.TRANSACTION ? (
                                sortedData.map((row, index) => {
                                    const cardId = row._id || `tx-card-${index}`;
                                    const isExpanded = isCardExpanded(cardId);
                                    return (
                                        <div
                                            key={cardId}
                                            className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'shadoww' : 'shadoww'}`}
                                        >
                                            {/* Card Header - Always Visible */}
                                            <div
                                                className='p-4 cursor-pointer select-none'
                                                onClick={() => toggleCard(cardId)}
                                                role="button"
                                                tabIndex={0}
                                                aria-expanded={isExpanded}
                                                aria-controls={`transaction-details-${cardId}`}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        toggleCard(cardId);
                                                    }
                                                }}
                                            >
                                                <div className='flex justify-between items-start gap-3'>
                                                    <div className='flex-1 min-w-0'>
                                                        <div className='flex items-center gap-1 mb-1'>
                                                            <span className='text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full'>#{index + 1}</span>
                                                            <span className='text-sm font-semibold text-text-body truncate'>{asText(row.item_id)}</span>
                                                        </div>
                                                        <p className='text-sm text-gray-600 truncate'>{asText(row.client)}</p>
                                                    </div>
                                                    <div className='flex items-center gap-3'>
                                                        <span className='text-base font-bold text-teal-500 mt-1'>${formatCurrency2(row.amount)}</span>
                                                        <button
                                                            className={`w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-text-text-400 focus:ring-offset-1 ${isExpanded ? 'rotate-180' : ''}`}
                                                            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleCard(cardId);
                                                            }}
                                                        >
                                                            <MdExpandMore className='w-5 h-5 text-gray-600' />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Preview Info - Always Visible */}
                                                <div className='flex items-center gap-4 mt-3 text-xs text-gray-500'>
                                                    <span className='flex items-center gap-1'>
                                                        <span className='w-1.5 h-1.5 rounded-full bg-cerulean-500'></span>
                                                        {asText(row.order_date ? moment(row.order_date).format('MMM D, YYYY') : '-')}
                                                    </span>
                                                    <span className='flex items-center gap-1'>
                                                        <span className='w-1.5 h-1.5 rounded-full bg-gray-300'></span>
                                                        Qty: {asText(toNum(row.qty).toFixed(0))}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Expandable Details Section */}
                                            <div
                                                id={`transaction-details-${cardId}`}
                                                className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                                                aria-hidden={!isExpanded}
                                            >
                                                <div className='px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/50'>
                                                    <div className='grid grid-cols-2 gap-4 text-sm'>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Check #</span>
                                                            <p className='font-medium text-gray-700'>{asText(toNum(row.check_number))}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Order Date</span>
                                                            <p className='font-medium text-gray-700'>{asText(row.order_date ? moment(row.order_date).format('MMMM D, YYYY') : '-')}</p>
                                                        </div>
                                                        <div className='col-span-2 space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Description</span>
                                                            <p className='font-medium text-gray-700 leading-relaxed'>{asText(row.description)}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Quantity</span>
                                                            <p className='font-medium text-gray-700'>{asText(toNum(row.qty).toFixed(0))}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Tax Status</span>
                                                            <p className='font-medium'>
                                                                {row.is_tax ? (
                                                                    <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold'>
                                                                        <span className='w-1.5 h-1.5 rounded-full bg-green-500'></span>
                                                                        TRUE
                                                                    </span>
                                                                ) : (
                                                                    <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold'>
                                                                        <span className='w-1.5 h-1.5 rounded-full bg-gray-400'></span>
                                                                        FALSE
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                sortedData.map((row, index) => {
                                    const cardId = row._id || `guest-card-${index}`;
                                    const isExpanded = isCardExpanded(cardId);
                                    return (
                                        <div
                                            key={cardId}
                                            className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-300 ease-in-out hover:shadow-md ${isExpanded ? 'shadow-lg' : 'shadow-sm'}`}
                                        >
                                            {/* Card Header - Always Visible */}
                                            <div
                                                className='p-4 cursor-pointer select-none'
                                                onClick={() => toggleCard(cardId)}
                                                role="button"
                                                tabIndex={0}
                                                aria-expanded={isExpanded}
                                                aria-controls={`guest-details-${cardId}`}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        toggleCard(cardId);
                                                    }
                                                }}
                                            >
                                                <div className='flex justify-between items-start gap-3'>
                                                    <div className='flex-1 min-w-0'>
                                                        <div className='flex items-center gap-1 mb-1'>
                                                            <span className='text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full'>#{index + 1}</span>
                                                            <span className='text-sm font-semibold text-text-body truncate'>{asText(row.guest_name)}</span>
                                                        </div>
                                                        <p className='text-sm text-gray-500 truncate'>{asText(row.email)}</p>
                                                    </div>
                                                    <div className='flex items-center gap-3'>
                                                        <div className='text-right'>
                                                            <span className='text-base font-bold text-teal-500'>${formatCurrency2(row.lifetime_spend)}</span>
                                                            {/* <p className='text-xs text-gray-500'>lifetime</p> */}
                                                        </div>
                                                        <button
                                                            className={`w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cerulean-500 focus:ring-offset-1 ${isExpanded ? 'rotate-180' : ''}`}
                                                            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleCard(cardId);
                                                            }}
                                                        >
                                                            <MdExpandMore className='w-5 h-5 text-gray-600' />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Preview Info - Always Visible */}
                                                <div className='flex items-center gap-4 mt-3 text-xs text-gray-500'>
                                                    <span className='flex items-center gap-1'>
                                                        <span className='w-1.5 h-1.5 rounded-full bg-cerulean-500'></span>
                                                        {asText(toNum(row.completed_visits).toFixed(0))} visits
                                                    </span>
                                                    <span className='flex items-center gap-1'>
                                                        <span className='w-1.5 h-1.5 rounded-full bg-gray-300'></span>
                                                        {asText(row.company)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Expandable Details Section */}
                                            <div
                                                id={`guest-details-${cardId}`}
                                                className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
                                                aria-hidden={!isExpanded}
                                            >
                                                <div className='px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/50'>
                                                    <div className='grid grid-cols-2 gap-4 text-sm'>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Company</span>
                                                            <p className='font-medium text-gray-700'>{asText(row.company)}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Phone</span>
                                                            <p className='font-medium text-gray-700'>{asText(row.phone_number)}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Birthday</span>
                                                            <p className='font-medium text-gray-700'>{asText(row.birthday ? moment(row.birthday).format('MMMM D, YYYY') : '-')}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Anniversary</span>
                                                            <p className='font-medium text-gray-700'>{asText(row.anniversary ? moment(row.anniversary).format('MMMM D, YYYY') : '-')}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>First Visit</span>
                                                            <p className='font-medium text-gray-700'>{asText(row.first_visit_date ? moment(row.first_visit_date).format('MMM D, YYYY') : '-')}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Recent Visit</span>
                                                            <p className='font-medium text-gray-700'>{asText(row.recent_visit_date ? moment(row.recent_visit_date).format('MMM D, YYYY') : '-')}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Completed Visits</span>
                                                            <p className='font-medium text-gray-700'>{asText(toNum(row.completed_visits).toFixed(0))}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Cancelled</span>
                                                            <p className='font-medium text-gray-700'>{asText(toNum(row.cancelled_count).toFixed(0))}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>No-Shows</span>
                                                            <p className='font-medium text-gray-700'>{asText(toNum(row.no_show_count).toFixed(0))}</p>
                                                        </div>
                                                        <div className='space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Per Cover Spend</span>
                                                            <p className='font-medium text-gray-700'>${formatCurrency2(row.lifetime_per_cover_spend)}</p>
                                                        </div>
                                                        <div className='col-span-2 space-y-1'>
                                                            <span className='text-xs text-gray-500 uppercase tracking-wider font-medium'>Marketing Opt-In</span>
                                                            <p className='font-medium'>
                                                                {row.marketing_opt_in ? (
                                                                    <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold'>
                                                                        <span className='w-1.5 h-1.5 rounded-full bg-green-500'></span>
                                                                        Subscribed
                                                                    </span>
                                                                ) : (
                                                                    <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold'>
                                                                        <span className='w-1.5 h-1.5 rounded-full bg-gray-400'></span>
                                                                        Not Subscribed
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}

                            {/* Mobile Total Card - Sticky Bottom */}
                            <div className='sticky bottom-0 bg-white rounded-xl border border-gray-200 shadow-lg p-4 mt-6 mb-2'>
                                <div className='flex items-center justify-between mb-3'>
                                    <h3 className='font-bold text-text-body text-base'>Total Summary</h3>
                                    <span className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full'>{sortedData.length} items</span>
                                </div>
                                {activeViewType === VIEW_TYPES.TRANSACTION ? (
                                    <div className='grid grid-cols-2 gap-4'>
                                        <div className='bg-green-50 rounded-lg p-3'>
                                            <span className='text-xs text-green-600 uppercase tracking-wider font-semibold block mb-1'>Total Amount</span>
                                            <span className='text-base font-bold text-green-700'>${formatCurrency2(totals.amount)}</span>
                                        </div>
                                        <div className='bg-cerulean-50 rounded-lg p-3'>
                                            <span className='text-xs text-cerulean-600 uppercase tracking-wider font-semibold block mb-1'>Total Quantity</span>
                                            <span className='text-base font-bold text-cerulean-700'>{totals.qty.toFixed(0)}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className='grid grid-cols-2 gap-4'>
                                        <div className='bg-cerulean-50 rounded-lg p-3'>
                                            <span className='text-xs text-cerulean-600 uppercase tracking-wider font-semibold block mb-1'>Completed Visits</span>
                                            <span className='text-base font-bold text-cerulean-700'>{totals.completed_visits.toFixed(0)}</span>
                                        </div>
                                        <div className='bg-green-50 rounded-lg p-3'>
                                            <span className='text-xs text-green-600 uppercase tracking-wider font-semibold block mb-1'>Lifetime Spend</span>
                                            <span className='text-base font-bold text-green-700'>${formatCurrency2(totals.lifetime_spend)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className='flex flex-col justify-center items-center h-64 text-center px-4'>
                            <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
                                <MdChevronRight className='w-8 h-8 text-gray-500' />
                            </div>
                            <p className='text-gray-500 font-medium'>No {activeViewType === VIEW_TYPES.TRANSACTION ? 'transaction' : 'guest'} data found</p>
                            <p className='text-sm text-gray-500 mt-1'>Try adjusting your filters or check back later</p>
                        </div>
                    )}
                </div>
                {/* <div className='flex items-center justify-between px-4 py-2'>
                        <p className='text-sm'>1-5 of 5</p>
                        <p className='text-sm'>All rows loaded</p>
                    </div> */}
            </div>
        </div>
    );
};

export default DailySalesHome;